import { useReplaySeek } from "@/hooks/useReplaySeek";
import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import api, { API } from "@/lib/api";
import { LoadingState, ErrorState } from "@/components/shared";
import { ReplayPlayer } from "@/components/ReplayPlayer";
import { ProctorIdentityBlock } from "@/components/ProctorIdentityBlock";
import { IntegrityTimeline } from "@/components/proctor/IntegrityTimeline";
import { VoiceSignal } from "@/components/proctor/VoiceSignal";
import { resumeCandidate } from "@/components/proctor/PausedCandidates";
import { MotionButton, useMotionPresets, DUR, EASE } from "@/lib/motion";
import { useSignedFiles } from "@/hooks/useSignedFiles";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Info, Sparkles, Video, Film, MonitorPlay, PauseCircle, Play, ChevronDown } from "lucide-react";

const DIGEST_UI = {
  review: { wrap: "border-amber-500/40 bg-amber-500/10", icon: "text-amber-400", title: "text-amber-300", label: "Human review suggested" },
  minor: { wrap: "border-sky-500/30 bg-sky-500/10", icon: "text-sky-400", title: "text-sky-300", label: "Minor signals — likely fine" },
  clean: { wrap: "border-emerald-500/30 bg-emerald-500/10", icon: "text-emerald-400", title: "text-emerald-300", label: "Looks clean" },
};

// Recruiter proctoring "session replay" for the full candidate profile — advisory only.
export function ProctorReplaySection({ candId }) {
  const [proctor, setProctor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [retry, setRetry] = useState(0);
  const [evidenceOpen, setEvidenceOpen] = useState(false);
  const evidenceRef = useRef(null);
  const { seek, replayRef, cameraRef, screenRef, seekNotice, jump, canSeek } = useReplaySeek(proctor);
  const token = localStorage.getItem("hs_token");
  const M = useMotionPresets();
  const signUrl = useSignedFiles([proctor?.recording?.path, proctor?.screen_recording?.path].filter(Boolean));

  useEffect(() => {
    let current = true;
    if (!retry) { setLoading(true); setProctor(null); }
    setError(false);
    if (candId) api.get(`/candidates/${candId}/proctor`).then(({ data }) => { if (current) setProctor(data); }).catch(() => { if (current) setError(true); }).finally(() => { if (current) setLoading(false); });
    else setLoading(false);
    return () => { current = false; };
  }, [candId, retry]);
  useEffect(() => {
    if (!proctor?.violations?.some((v) => v.ai_status === "pending" || (v.audio_path && v.audio_assessment === undefined))) return undefined;
    const t = setTimeout(() => setRetry((n) => n + 1), 5000);
    return () => clearTimeout(t);
  }, [proctor]);

  // Jumping to a moment lives inside the collapsed evidence panel — open it and scroll in.
  const handleJump = (v) => {
    setEvidenceOpen(true);
    jump(v);
    requestAnimationFrame(() => evidenceRef.current?.scrollIntoView({ behavior: M.reduce ? "auto" : "smooth", block: "nearest" }));
  };

  if (loading) return <LoadingState label="Loading session evidence…" />;
  if (error) return <ErrorState message="Could not load session evidence." onRetry={() => setRetry((n) => n + 1)} />;
  if (!proctor) return null;
  const hasMedia = proctor.recording?.path || proctor.screen_recording?.path || proctor.identity || (proctor.snapshots?.length || 0) > 0;
  const hasData = hasMedia || (proctor.violations?.length || 0) > 0 || proctor.consent?.alternative_path;
  if (!hasData) return null;
  const digest = proctor.digest ? (DIGEST_UI[proctor.digest.verdict] || DIGEST_UI.minor) : null;

  return (
    <motion.div
      variants={M.listContainer} initial="hidden" animate="show"
      className="rounded-xl border border-border/60 bg-card p-5 space-y-4" data-testid="ci-proctor-replay"
    >
      <motion.div variants={M.fadeInUp} className="space-y-1">
        <div className="text-[11px] uppercase font-mono tracking-[0.18em] text-muted-foreground">Integrity review</div>
        <div className="flex items-center gap-2 text-base font-display font-semibold"><Film className="h-4 w-4 text-primary" /> Proctoring session replay</div>
      </motion.div>

      <motion.div variants={M.fadeInUp} className="rounded-lg border border-blue-500/30 bg-blue-500/10 p-3 flex gap-2.5">
        <Info className="h-4 w-4 text-blue-400 shrink-0 mt-0.5" />
        <p className="text-xs text-blue-700 dark:text-blue-200 leading-relaxed">Signals are <strong>advisory only</strong> and never proof of misconduct or identity. Use them only to decide whether a human review is warranted.</p>
      </motion.div>

      {proctor.proctor_state?.paused && (
        <motion.div variants={M.popIn} className="rounded-lg border border-amber-400/30 bg-amber-500/10 p-3 flex items-center gap-3" data-testid="ci-paused-banner">
          <PauseCircle className="h-4 w-4 text-amber-400 shrink-0" />
          <p className="flex-1 text-xs text-amber-100/90">This candidate's test is paused under its monitoring rules. Their timer is stopped until you let them continue.</p>
          <MotionButton {...M.tap} size="sm" className="gap-1.5" data-testid="ci-let-continue" onClick={async () => { if (await resumeCandidate(candId)) setRetry((n) => n + 1); }}><Play className="h-3.5 w-3.5" /> Let them continue</MotionButton>
        </motion.div>
      )}

      {proctor.consent?.alternative_path && (
        <motion.div variants={M.fadeInUp} className="rounded-lg border border-emerald-500/30 bg-emerald-500/10 p-2.5 text-xs text-emerald-200" data-testid="ci-proctor-nonbiometric">Candidate chose the non-biometric path — no camera signals were collected.</motion.div>
      )}

      {digest && (
        <motion.div variants={M.popIn} className={`rounded-lg border p-3 flex items-start gap-2 ${digest.wrap}`} data-testid="ci-proctor-digest">
          <Sparkles className={`h-4 w-4 shrink-0 mt-0.5 ${digest.icon}`} />
          <div><div className={`text-xs font-semibold ${digest.title}`}>{digest.label}</div><p className="text-xs text-muted-foreground leading-relaxed">{proctor.digest.summary}</p></div>
        </motion.div>
      )}

      {proctor.voice_signal && (
        <motion.div variants={M.popIn}>
          <VoiceSignal signal={proctor.voice_signal} candId={candId} recruiter />
        </motion.div>
      )}

      {proctor.total > 0 && (
        <motion.div variants={M.fadeInUp} className="grid grid-cols-3 gap-2" data-testid="ci-proctor-severity">
          {["high", "medium", "low"].map((s) => (
            <div key={s} className="rounded-lg border border-border bg-card p-2 text-center">
              <div className={`text-lg font-mono font-bold ${s === "high" ? "text-rose-400" : s === "medium" ? "text-amber-400" : "text-emerald-400"}`}>{proctor.severity_counts?.[s] || 0}</div>
              <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{s}</div>
            </div>
          ))}
        </motion.div>
      )}

      {seekNotice && <p role="status" className="text-xs text-primary" data-testid="replay-seek-notice">{seekNotice}</p>}

      <motion.div variants={M.fadeInUp}>
        <IntegrityTimeline candId={candId} violations={proctor.violations || []} token={token} jump={handleJump} canSeek={canSeek} onChanged={() => setRetry((n) => n + 1)} />
      </motion.div>

      {hasMedia && (
        <motion.div variants={M.fadeInUp}>
          <Collapsible open={evidenceOpen} onOpenChange={setEvidenceOpen}>
            <CollapsibleTrigger
              ref={evidenceRef}
              className="group flex w-full items-center justify-between rounded-lg border border-border bg-muted/40 px-3 py-2.5 text-sm font-medium hover:border-primary/40 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              data-testid="ci-evidence-toggle"
            >
              <span className="inline-flex items-center gap-2"><Video className="h-4 w-4 text-primary" /> Session evidence &amp; recordings</span>
              <ChevronDown className="h-4 w-4 text-muted-foreground transition-transform duration-200 group-data-[state=open]:rotate-180" />
            </CollapsibleTrigger>
            <CollapsibleContent className="overflow-hidden data-[state=open]:animate-accordion-down data-[state=closed]:animate-accordion-up">
              <div className="space-y-4 pt-4">
                {proctor.recording?.path && (
                  <div className="space-y-2" data-testid="ci-proctor-recording">
                    <div className="text-[11px] uppercase tracking-wider text-muted-foreground flex items-center gap-1.5"><Video className="h-3.5 w-3.5" /> Full session recording</div>
                    <video ref={cameraRef} data-testid="replay-camera-video" controls src={signUrl(proctor.recording.path)} className="w-full rounded-xl border border-white/10 bg-black" />
                  </div>
                )}
                {proctor.screen_recording?.path && (
                  <div className="space-y-2" data-testid="ci-proctor-screen-recording">
                    <div className="text-[11px] uppercase tracking-wider text-muted-foreground flex items-center gap-1.5"><MonitorPlay className="h-3.5 w-3.5" /> Screen recording</div>
                    <video ref={screenRef} data-testid="replay-screen-video" controls src={signUrl(proctor.screen_recording.path)} className="w-full rounded-xl border border-white/10 bg-black" />
                  </div>
                )}
                {proctor.identity && <ProctorIdentityBlock candId={candId} identity={proctor.identity} apiBase={API} token={token} />}
                {proctor.snapshots?.length > 0 && (
                  <div ref={replayRef}><ReplayPlayer frames={proctor.snapshots} apiBase={API} token={token} seekTo={seek.idx} seekNonce={seek.nonce} /></div>
                )}
              </div>
            </CollapsibleContent>
          </Collapsible>
        </motion.div>
      )}
    </motion.div>
  );
}
